import { Toast } from '@viapeople/components'
import React, { createContext, type ReactNode, useContext, useState } from 'react'

type ToastType = 'info' | 'error'

interface ToastData {
	message: string
	type: ToastType
	id: number
}

interface ToastContextType {
	showToast: (message: string, type?: ToastType) => void
	hideToast: (id: number) => void
	toasts: ToastData[]
}

const ToastContext = createContext<ToastContextType | undefined>(undefined)

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
	const [toasts, setToasts] = useState<ToastData[]>([])
	const idCounterRef = React.useRef(0)

	const showToast = (message: string, type: ToastType = 'info') => {
		// Use a counter to ensure unique IDs even when toasts are created rapidly
		const id = ++idCounterRef.current
		const newToast = { message, type, id }

		setToasts((prev) => [...prev, newToast])

		setTimeout(() => {
			hideToast(id)
		}, 5000)

		return id
	}

	const hideToast = (id: number) => {
		setToasts((prev) => prev.filter((toast) => toast.id !== id))
	}

	return (
		<ToastContext.Provider value={{ showToast, hideToast, toasts }}>
			{children}
			<div className='toast-container '>
				{toasts.map((toast) => (
					<div key={toast.id}>
						<Toast
							variant={toast.type}
							message={toast.message}
							position='bottomLeft'
							onClose={() => hideToast(toast.id)}
						/>
					</div>
				))}
			</div>
		</ToastContext.Provider>
	)
}

export const useToast = () => {
	const context = useContext(ToastContext)
	if (context === undefined) {
		return {
			showToast: () => {},
			hideToast: () => {},
			toasts: [],
		}
	}
	return context
}
